/**
 * dashboard/leveling-guide.tsx — Halaman penjelasan leveling untuk tutor (N-23).
 *
 * Lives at `/dashboard/leveling-guide`. Auth-gated to approved tutors.
 * Non-tutors (admin/parent/student/visitor) get redirected to their landing.
 * Content rendered via `LevelingGuide` (no fetch, fully static).
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { LevelingGuide } from '../../components/tutor/LevelingGuide.js';
import { freshSessionQuery } from '../../lib/api/queries.js';

export const Route = createFileRoute('/dashboard/leveling-guide')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: LevelingGuidePage,
});

function LevelingGuidePage(): ReactNode {
  const session = useQuery(freshSessionQuery);
  const userName = session.data?.ok ? session.data.data.user?.name : undefined;

  return (
    <main className="page-shell" data-testid="leveling-guide-page">
      <PageHeader
        eyebrow="Panduan mengajar"
        title="Cara baca leveling game"
        description="Kenapa game kami punya banyak level, dan bagaimana memakainya untuk mengajar."
      />

      <LevelingGuide tutorName={userName} />
    </main>
  );
}
